// ============================================================================
// Admin Dashboard tRPC Hooks
// ============================================================================
// Custom hooks wrapping tRPC for admin/executive dashboard data.
// These hooks connect to live Snowflake data via the admin.* tRPC endpoints.
// ============================================================================

import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '../../lib/trpc';
import type { AdminTimePeriod, PLTimeGranularity, AdminPLStatementGranularity, SkuSummarySortBy, SortOrder, SkuSummaryDateRange } from '@globalbridge/types';

// ============================================================================
// Query Configuration Constants
// ============================================================================
// Centralized query options to ensure consistent caching behavior across hooks.
// ============================================================================

/** Standard query options for admin dashboard data (1 minute cache, auto-refresh) */
const ADMIN_QUERY_OPTIONS = {
  staleTime: 60 * 1000,           // Data fresh for 1 minute
  refetchInterval: 60 * 1000,     // Auto-refresh every 60 seconds
  refetchOnWindowFocus: true,
} as const;

/** Query options for real-time/live data (30 second cache) */
const REALTIME_QUERY_OPTIONS = {
  staleTime: 30 * 1000,           // Shorter stale time for real-time data
  refetchInterval: 60 * 1000,     // Poll every 60 seconds
  refetchOnWindowFocus: true,
} as const;

/** Query options for static/reference data (5 minute cache, no auto-refresh) */
const STATIC_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,       // Data fresh for 5 minutes
  refetchOnWindowFocus: false,
} as const;

/** Query options for paginated data (1 minute cache, keeps previous data for smooth transitions) */
const PAGINATED_QUERY_OPTIONS = {
  staleTime: 60 * 1000,
  refetchOnWindowFocus: true,
  placeholderData: keepPreviousData,
} as const;

/**
 * Hook to fetch the executive dashboard data
 *
 * Returns all metrics for the CEO dashboard including:
 * - Yesterday's sales and comparison metrics
 * - MoM growth and return rate
 * - Sales by brand (filtered by period)
 * - Inventory position and risk
 * - Trend data for charts (filtered by period)
 *
 * Auto-refreshes every 60 seconds.
 *
 * @param period - Time period filter ('7d', '30d', '90d', 'mtd', 'ytd')
 * @param startDate - Optional custom start date (YYYY-MM-DD)
 * @param endDate - Optional custom end date (YYYY-MM-DD)
 * @returns tRPC query result with dashboard data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useAdminDashboard('mtd');
 * if (isLoading) return <Skeleton />;
 * return <SalesMetrics data={data.salesYesterday} />;
 * ```
 */
export function useAdminDashboard(
  period: AdminTimePeriod = 'mtd',
  startDate?: string,
  endDate?: string,
) {
  return trpc.admin.dashboard.useQuery(
    {
      period,
      ...(startDate && endDate ? { startDate, endDate } : {}),
    },
    ADMIN_QUERY_OPTIONS,
  );
}

/**
 * Hook to fetch real-time inventory position
 *
 * Returns current inventory snapshot for polling.
 * Use this for the live inventory widget that updates frequently.
 *
 * Auto-refreshes every 60 seconds.
 *
 * @returns tRPC query result with inventory data
 *
 * @example
 * ```tsx
 * const { data: inventory } = useAdminInventory();
 * return <InventoryWidget totalUnits={inventory?.totalUnits} />;
 * ```
 */
export function useAdminInventory() {
  return trpc.admin.inventory.useQuery(undefined, REALTIME_QUERY_OPTIONS);
}

/**
 * Hook to fetch all brands with summary metrics
 *
 * Returns list of all brands with their key performance metrics
 * for the brands overview page.
 *
 * @returns tRPC query result with brands list
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useAdminBrands();
 * return <BrandsTable brands={data?.brands} />;
 * ```
 */
export function useAdminBrands() {
  return trpc.admin.brands.useQuery(undefined, ADMIN_QUERY_OPTIONS);
}

/**
 * Hook to fetch detailed metrics for a specific brand
 *
 * @param brandId - Brand ID to fetch details for
 * @returns tRPC query result with brand detail
 *
 * @example
 * ```tsx
 * const { data: brand } = useAdminBrandDetail(1);
 * return <BrandHeader name={brand?.name} />;
 * ```
 */
export function useAdminBrandDetail(brandId: number | undefined) {
  return trpc.admin.brandDetail.useQuery(
    { brandId: brandId! },
    {
      enabled: brandId !== undefined,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );
}

/**
 * Hook to fetch Profit & Loss data
 *
 * Returns aggregated P&L data for the business including:
 * - Summary: Current period highlights with comparisons
 * - Data: Historical P&L data rows
 *
 * @param granularity - Time granularity ('monthly', 'quarterly', 'annually')
 * @returns tRPC query result with P&L data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useAdminProfitLoss('monthly');
 * return <PLTable data={data?.data} />;
 * ```
 */
export function useAdminProfitLoss(granularity: PLTimeGranularity = 'monthly') {
  return trpc.admin.profitLoss.useQuery(
    { granularity },
    {
      staleTime: 5 * 60 * 1000, // P&L data is less volatile, 5 minute stale time
      refetchOnWindowFocus: false,
    },
  );
}

// ============================================================================
// Brand CRUD Hooks
// ============================================================================

/**
 * Hook to fetch all brands for management (includes user/store counts)
 * Uses Prisma-only data
 */
export function useAdminAllBrands() {
  return trpc.admin.allBrands.useQuery(undefined, REALTIME_QUERY_OPTIONS);
}

/**
 * Hook to fetch brands from Snowflake with metrics for Brand Manager
 * Includes: active SKUs, inventory, sales MTD, sync status
 */
export function useBrandsForManagement() {
  return trpc.admin.brandsForManagement.useQuery(undefined, ADMIN_QUERY_OPTIONS);
}

/**
 * Hook to create a new brand
 */
export function useCreateBrand() {
  const utils = trpc.useUtils();

  return trpc.admin.createBrand.useMutation({
    onSuccess: () => {
      utils.admin.allBrands.invalidate();
      utils.admin.brands.invalidate();
      // The catalog brand picker reads this roster. Refresh it before a newly
      // created brand can become the selected catalog brand.
      utils.users.myBrands.invalidate();
      // The management list is where the new brand has to appear — a brand with
      // no Snowflake id yet exists ONLY in the registry, so without this the
      // admin who just created it sees an unchanged list.
      utils.admin.brandsForManagement.invalidate();
    },
  });
}

/**
 * Hook to move a brand off its placeholder Snowflake id onto the real one
 */
export function useReassignSnowflakeId() {
  const utils = trpc.useUtils();

  return trpc.admin.reassignSnowflakeId.useMutation({
    onSuccess: () => {
      utils.admin.allBrands.invalidate();
      utils.admin.brands.invalidate();
      utils.admin.brandsForManagement.invalidate();
    },
  });
}

/**
 * Hook to update a brand
 */
export function useUpdateBrand() {
  const utils = trpc.useUtils();

  return trpc.admin.updateBrand.useMutation({
    onSuccess: () => {
      utils.admin.allBrands.invalidate();
      utils.admin.brands.invalidate();
      utils.admin.brandsForManagement.invalidate();
    },
  });
}

/**
 * Hook to delete a brand
 */
export function useDeleteBrand() {
  const utils = trpc.useUtils();

  return trpc.admin.deleteBrand.useMutation({
    onSuccess: () => {
      utils.admin.allBrands.invalidate();
      utils.admin.brands.invalidate();
      utils.admin.brandsForManagement.invalidate();
    },
  });
}

// ============================================================================
// Channel/Marketplace Sales Hooks
// ============================================================================

/**
 * Hook to fetch sales by channel/marketplace
 *
 * Returns sales data grouped by marketplace for visualization in
 * stacked area charts and summary tables.
 *
 * @param period - Time period filter ('7d', '30d', '90d', 'mtd', 'ytd')
 * @param startDate - Optional custom start date (YYYY-MM-DD)
 * @param endDate - Optional custom end date (YYYY-MM-DD)
 * @returns tRPC query result with channel sales data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSalesByChannel('mtd');
 * return <ChannelSalesChart data={data?.trend} />;
 * ```
 */
export function useSalesByChannel(
  period: AdminTimePeriod = 'mtd',
  startDate?: string,
  endDate?: string,
) {
  return trpc.admin.salesByChannel.useQuery(
    {
      period,
      ...(startDate && endDate ? { startDate, endDate } : {}),
    },
    ADMIN_QUERY_OPTIONS,
  );
}

// ============================================================================
// Sales Transactions Hooks (Reports Tab)
// ============================================================================

/**
 * Hook to fetch sales transactions filtered by shipment date
 *
 * Returns detailed sales transaction records for the Reports tab.
 * Only includes records where ACTUALSHIPDATE is not null.
 *
 * Supports both period-based and custom date range filtering.
 *
 * @param options - Query options
 * @param options.period - Time period filter ('7d', '30d', '90d', 'mtd', 'ytd')
 * @param options.startDate - Custom start date (YYYY-MM-DD), overrides period
 * @param options.endDate - Custom end date (YYYY-MM-DD), overrides period
 * @param options.limit - Max records per page (default 100)
 * @param options.offset - Pagination offset
 * @param options.dateFilterField - Which date field to filter by ('shipDate' or 'salesDate')
 * @returns tRPC query result with transactions data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSalesTransactions({
 *   period: '30d',
 *   limit: 100,
 *   offset: 0,
 *   dateFilterField: 'shipDate',
 * });
 * return <TransactionsTable data={data?.transactions} />;
 * ```
 */
export function useSalesTransactions(options: {
  period?: AdminTimePeriod;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  dateFilterField?: 'shipDate' | 'salesDate';
}) {
  return trpc.admin.salesTransactions.useQuery(options, PAGINATED_QUERY_OPTIONS);
}

// ============================================================================
// Schema Discovery Hooks
// ============================================================================

/**
 * Hook to discover available Snowflake tables and views
 *
 * @returns tRPC query result with tables and views lists
 */
export function useDiscoverSchema() {
  return trpc.admin.discoverSchema.useQuery(undefined, STATIC_QUERY_OPTIONS);
}

/**
 * Hook to describe a table's columns
 *
 * @param tableName - Name of the table to describe
 * @returns tRPC query result with column info
 */
export function useDescribeTable(tableName: string) {
  return trpc.admin.describeTable.useQuery(
    { tableName },
    { enabled: !!tableName, ...STATIC_QUERY_OPTIONS },
  );
}

/**
 * Hook to sample data from a table
 *
 * @param tableName - Name of the table to sample
 * @param limit - Number of rows to return (default 5)
 * @returns tRPC query result with sample rows
 */
export function useSampleTable(tableName: string, limit: number = 5) {
  return trpc.admin.sampleTable.useQuery(
    { tableName, limit },
    { enabled: !!tableName, ...STATIC_QUERY_OPTIONS, staleTime: 60 * 1000 },
  );
}

// ============================================================================
// Inventory Report Hooks
// ============================================================================

/**
 * Hook to fetch Low Inventory Report
 *
 * @param options.limit - Max records per page (default 100)
 * @param options.offset - Pagination offset
 * @returns tRPC query result with low inventory data, summary, and brand breakdown
 */
export function useLowInventoryReport(options: {
  limit?: number;
  offset?: number;
  enabled?: boolean;
} = {}) {
  return trpc.admin.lowInventoryReport.useQuery(
    { limit: options.limit, offset: options.offset },
    { enabled: options.enabled !== false, ...PAGINATED_QUERY_OPTIONS },
  );
}

/**
 * Hook to fetch Out of Stock Report
 *
 * @param options.limit - Max records per page (default 100)
 * @param options.offset - Pagination offset
 * @returns tRPC query result with out of stock data, summary, and brand breakdown
 */
export function useOutOfStockReport(options: {
  limit?: number;
  offset?: number;
  enabled?: boolean;
} = {}) {
  return trpc.admin.outOfStockReport.useQuery(
    { limit: options.limit, offset: options.offset },
    { enabled: options.enabled !== false, ...PAGINATED_QUERY_OPTIONS },
  );
}

/**
 * Hook to fetch Storage Billing Detail Report
 *
 * @param options.startDate - Start date (YYYY-MM-DD)
 * @param options.endDate - End date (YYYY-MM-DD)
 * @param options.limit - Max records per page (default 100)
 * @param options.offset - Pagination offset
 * @returns tRPC query result with storage billing detail data and summary
 */
export function useStorageBillingDetail(options: {
  startDate: string;
  endDate: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  return trpc.admin.storageBillingDetail.useQuery(
    {
      startDate: options.startDate,
      endDate: options.endDate,
      limit: options.limit,
      offset: options.offset,
    },
    {
      enabled: options.enabled !== false && !!options.startDate && !!options.endDate,
      ...PAGINATED_QUERY_OPTIONS,
    },
  );
}

/**
 * Hook to fetch Storage Billing Summary Report
 *
 * @param options.startDate - Start date (YYYY-MM-DD)
 * @param options.endDate - End date (YYYY-MM-DD)
 * @param options.limit - Max records per page (default 100)
 * @param options.offset - Pagination offset
 * @returns tRPC query result with storage billing summary data, totals, and brand breakdown
 */
export function useStorageBillingSummary(options: {
  startDate: string;
  endDate: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  return trpc.admin.storageBillingSummary.useQuery(
    {
      startDate: options.startDate,
      endDate: options.endDate,
      limit: options.limit,
      offset: options.offset,
    },
    {
      enabled: options.enabled !== false && !!options.startDate && !!options.endDate,
      ...PAGINATED_QUERY_OPTIONS,
    },
  );
}

/**
 * Hook to fetch Restock Priority Report
 *
 * Returns low/out-of-stock products ranked by sales velocity.
 * Combines inventory status with sales data to prioritize restocking.
 *
 * @param options.period - Number of days for sales lookback (7, 30, or 90)
 * @param options.limit - Max records per page (default 100)
 * @param options.offset - Pagination offset
 * @returns tRPC query result with priority items, summary, and pagination
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useRestockPriority({
 *   period: 30,
 *   limit: 100,
 *   offset: 0,
 * });
 * // data.items contains products with priorityTier: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
 * // data.summary contains totalItems, criticalCount, totalRevenueAtRisk
 * ```
 */
export function useRestockPriority(options: {
  period?: 7 | 30 | 90;
  limit?: number;
  offset?: number;
  enabled?: boolean;
} = {}) {
  return trpc.admin.restockPriority.useQuery(
    {
      period: options.period ?? 30,
      limit: options.limit,
      offset: options.offset,
    },
    { enabled: options.enabled !== false, ...PAGINATED_QUERY_OPTIONS },
  );
}

// ============================================================================
// Return Transactions Hooks (Reports Tab)
// ============================================================================

/**
 * Hook to fetch return transactions from DAILY_RETURN
 */
export function useReturnTransactions(options: {
  period?: AdminTimePeriod;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  return trpc.admin.returnTransactions.useQuery(options, PAGINATED_QUERY_OPTIONS);
}

// ============================================================================
// SKU Summary Hooks
// ============================================================================

/**
 * Hook to fetch SKU Summary
 *
 * Returns paginated list of SKUs with sales and current inventory.
 * Supports filtering by brand and date range, and sorting by various columns.
 *
 * @param options.brandId - Optional brand filter
 * @param options.dateRange - Date range filter ('30d', '60d', '90d', 'all')
 * @param options.sortBy - Column to sort by
 * @param options.sortOrder - 'asc' or 'desc'
 * @param options.limit - Max records per page (default 20)
 * @param options.offset - Pagination offset (default 0)
 * @param options.enabled - Whether to enable the query (default true)
 * @returns tRPC query result with SKU summary data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSkuSummary({
 *   brandId: 123,
 *   dateRange: '30d',
 *   sortBy: 'totalRevenue',
 *   sortOrder: 'desc',
 *   limit: 20,
 *   offset: 0,
 * });
 * return <SKUSummaryTable data={data?.items} />;
 * ```
 */
export function useSkuSummary(options: {
  brandId?: number;
  dateRange?: SkuSummaryDateRange;
  sortBy?: SkuSummarySortBy;
  sortOrder?: SortOrder;
  limit?: number;
  offset?: number;
  enabled?: boolean;
} = {}) {
  return trpc.admin.skuSummary.useQuery(
    {
      brandId: options.brandId,
      dateRange: options.dateRange ?? 'all',
      sortBy: options.sortBy ?? 'totalRevenue',
      sortOrder: options.sortOrder ?? 'desc',
      limit: options.limit ?? 20,
      offset: options.offset ?? 0,
    },
    {
      enabled: options.enabled !== false,
      ...PAGINATED_QUERY_OPTIONS,
    },
  );
}

// ============================================================================
// P&L Statement Hooks (By Brand, Full Fee Breakdown)
// ============================================================================

/**
 * Hook to fetch P&L Statement by brand with full fee breakdown
 *
 * @param granularity - Time granularity ('weekly', 'monthly', 'quarterly', 'annually')
 * @param startDate - Optional custom start date (YYYY-MM-DD)
 * @param endDate - Optional custom end date (YYYY-MM-DD)
 */
export function useAdminPLStatement(
  granularity: AdminPLStatementGranularity,
  startDate?: string,
  endDate?: string,
) {
  return trpc.admin.plStatement.useQuery(
    {
      granularity,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * Hook to fetch P&L by marketplace across all brands (admin only)
 */
export function useAdminPLByMarketplace(
  granularity: AdminPLStatementGranularity,
  startDate?: string,
  endDate?: string,
  enabled = true,
) {
  return trpc.admin.plByMarketplace.useQuery(
    {
      granularity,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * Hook to fetch P&L brand drill-down (per-marketplace breakdown)
 *
 * @param brandId - Brand ID to drill into (null to disable)
 * @param granularity - Time granularity
 * @param startDate - Optional custom start date
 * @param endDate - Optional custom end date
 */
export function useAdminPLBrandDrilldown(
  brandId: number | null,
  granularity: AdminPLStatementGranularity,
  startDate?: string,
  endDate?: string,
) {
  return trpc.admin.plBrandDrilldown.useQuery(
    {
      brandId: brandId!,
      granularity,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    },
    {
      enabled: brandId !== null,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * Admin-only: replace the full set of brands a user can access.
 */
export function useSetUserBrands() {
  const utils = trpc.useUtils();
  return trpc.admin.setUserBrands.useMutation({
    onSuccess: (_res, variables) => {
      utils.users.getById.invalidate({ id: variables.userId });
      utils.users.list.invalidate();
      utils.users.myBrands.invalidate();
    },
  });
}

